import { useStore } from '@xyflow/react'
import { useCallback } from 'react'

// Module-level cache for hasChildren results per node
const hasChildrenCache = new Map<string, boolean>()
const hasChildrenSignatureCache = new Map<string, string>()

/**
 * Custom hook that efficiently checks if a node has children.
 *
 * The problem: Calling getEdges() on every render and iterating through all edges
 * is wasteful when we only care about whether THIS node has at least one child.
 *
 * The solution: useStore with a selector + equality function that only triggers
 * re-renders when the hasChildren status actually changes for this node.
 *
 * @param nodeId - The ID of the node to check
 * @returns Whether the node has at least one visible child
 */
export function useHasChildren(nodeId: string): boolean {
  const selector = useCallback(
    (state: { edges: Array<{ source: string; target: string; hidden?: boolean }> }) => {
      const visibleOutgoingCount = state.edges.filter(
        (e) => e.source === nodeId && !e.hidden
      ).length

      const signature = `${nodeId}:${visibleOutgoingCount}`
      const cachedSignature = hasChildrenSignatureCache.get(nodeId)

      if (signature === cachedSignature) {
        return hasChildrenCache.get(nodeId) ?? false
      }

      const result = visibleOutgoingCount > 0
      hasChildrenSignatureCache.set(nodeId, signature)
      hasChildrenCache.set(nodeId, result)

      return result
    },
    [nodeId]
  )

  const equalityFn = useCallback((a: boolean, b: boolean) => a === b, [])
  return useStore(selector, equalityFn)
}
